import { Features } from '@backend/lib-feature-mgr';
import Image from 'next/image';
import { forwardRef } from 'react';
import { styled } from 'styled-components';

import { Link } from '~/components/Link';
import { Menu } from '~/components/Menu/Menu';
import { useCheckFeatureEnabled, useIsPromanBusinessUnit } from '~/hooks';
import { useBrandName } from '~/hooks/useBrandName';
import { useHref } from '~/hooks/useHref';
import { getMediaQuery } from '~/utils/responsive/media';

const StyledNavbarContainer = styled.div`
  height: 74px;

  display: flex;
  position: relative;
  align-items: center;

  gap: 32px;
  padding: 0 32px;
`;

// sized so that the header does not shift while the logo loads; next/image serves the PNG at the displayed size.
// eager: next/image lazy-loads by default, and the logo is above the fold on every page
const StyledNavbarLogo = styled(Image)`
  object-fit: contain;
  width: 120px;
  height: auto;
  cursor: pointer;

  ${getMediaQuery('mobile')} {
    width: 100px;
  }

  &.desktop-logo {
    ${getMediaQuery('mobile')} {
      display: none;
    }
  }

  &.mobile-logo {
    display: none;
    ${getMediaQuery('mobile')} {
      display: block !important;
      width: 2.5rem;
    }
  }
`;

const StyledNavbar = styled.header`
  position: relative;
  width: 100%;
  background: #272356;
  font-family: 'Red Hat Display', sans-serif;
`;

export const Navbar = forwardRef<HTMLDivElement>((_, ref) => {
  const { baseHref } = useHref();
  const brandName = useBrandName();
  const isProman = useIsPromanBusinessUnit();
  const isSEOEnabled = useCheckFeatureEnabled(Features.JobBoardSEO);

  return (
    <StyledNavbar ref={ref}>
      <StyledNavbarContainer>
        <Link href={baseHref}>
          {isProman ? (
            <StyledNavbarLogo
              src="/images/proman-logo.svg"
              alt={brandName}
              width={120}
              height={26}
              className="desktop-logo"
              loading="eager"
            />
          ) : (
            <>
              <StyledNavbarLogo
                src="/images/logo-white-iziwork-by-proman.png"
                alt="iziwork"
                width={120}
                height={36}
                className="desktop-logo"
                loading="eager"
              />
              <StyledNavbarLogo
                src="/images/Monogram.svg"
                alt="iziwork"
                width={42}
                height={30}
                className="mobile-logo"
                loading="eager"
              />
            </>
          )}
        </Link>
        {isSEOEnabled && <Menu />}
      </StyledNavbarContainer>
    </StyledNavbar>
  );
});
